/* Pure helpers for the illustrated route map — no heavy imports, safe to pull
   into the main bundle. */

/* Base canvas size for a typical 3-8 stop safari. RouteMap.jsx's own
   viewBox grows for a denser itinerary — but a bigger viewBox squeezed
   into the SAME on-page width just renders everything smaller (text,
   markers, gaps all shrink together), which looks like a "small map", the
   opposite of the intended fix. TourItinerary.jsx uses this same function
   to widen the actual page container the map sits in, so a bigger viewBox
   is matched by a bigger rendered size instead of being squashed into the
   old one. */
export const DEFAULT_MAP_W = 1280, DEFAULT_MAP_H = 820, MAX_MAP_W = 1900, MAX_MAP_H = 1500;

export function mapCanvasSize(stopCount) {
  const extra = Math.max(0, stopCount - 6);
  return {
    w: Math.min(MAX_MAP_W, DEFAULT_MAP_W + extra * 70),
    // Height was growing far more slowly than width (22px/stop vs 70px/stop)
    // — on a genuinely dense tour, a whole cluster of stops (Kilimanjaro's
    // camps, say) has nowhere near enough room to spread out vertically,
    // which is exactly what pushes some labels uncomfortably far from
    // their own marker just to avoid overlapping half a dozen others. The
    // page has an empty margin below the map anyway, so there's real room
    // to give this more height, not just more width.
    h: Math.min(MAX_MAP_H, DEFAULT_MAP_H + extra * 45),
  };
}

/** Deterministic seeded PRNG (mulberry32) — same tour → same-looking
 *  "hand-drawn" decoration every time it's viewed, but different tours get
 *  visibly different decoration instead of the exact same fixed pattern
 *  stamped onto every map regardless of what it's actually showing. */
export function mulberry32(seed) {
  let t = seed >>> 0;
  return function rand() {
    t += 0x6D2B79F5;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r = (r + Math.imul(r ^ (r >>> 7), 61 | r)) ^ r;
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

/** A small integer seed derived from a set of [lat,lng] stops — cheap
 *  stand-in for hashing the tour's actual identity without needing to pass
 *  a tour id/slug down into the map component. */
export function seedFromPoints(points) {
  let h = 17;
  for (const p of points) {
    h = (h * 31 + Math.round((p.lat || 0) * 1000)) | 0;
    h = (h * 31 + Math.round((p.lng || 0) * 1000)) | 0;
  }
  return h >>> 0;
}

/* ── Gateway gazetteer ─────────────────────────────────────────────────────── */
/* [keyword, [lat,lng], display name, country, feature type] */
export const GATEWAYS = [
  ['jomo kenyatta',  [-1.319, 36.928], 'Nairobi',       'Kenya',    'airport'],
  ['nairobi',        [-1.286, 36.817], 'Nairobi',       'Kenya',    'city'],
  ['mombasa',        [-4.043, 39.668], 'Mombasa',       'Kenya',    'city'],
  ['arusha',         [-3.387, 36.683], 'Arusha',        'Tanzania', 'city'],
  ['kilimanjaro',    [-3.429, 37.075], 'Kilimanjaro',   'Tanzania', 'airport'],
  ['moshi',          [-3.349, 37.340], 'Moshi',         'Tanzania', 'city'],
  ['dar es salaam',  [-6.792, 39.208], 'Dar es Salaam', 'Tanzania', 'city'],
  ['bagamoyo',       [-6.443, 38.904], 'Bagamoyo',      'Tanzania', 'city'],
  ['stone town',     [-6.163, 39.189], 'Stone Town',    'Tanzania', 'island'],
  ['nungwi',         [-5.726, 39.297], 'Nungwi',        'Tanzania', 'island'],
  ['zanzibar',       [-6.165, 39.199], 'Zanzibar',      'Tanzania', 'island'],
  ['dodoma',         [-6.163, 35.752], 'Dodoma',        'Tanzania', 'city'],
  ['mwanza',         [-2.517, 32.900], 'Mwanza',        'Tanzania', 'city'],
  ['entebbe',        [ 0.047, 32.443], 'Entebbe',       'Uganda',   'airport'],
  ['kampala',        [ 0.348, 32.583], 'Kampala',       'Uganda',   'city'],
  ['kigali',         [-1.944, 30.062], 'Kigali',        'Rwanda',   'city'],
  ['mto wa mbu',     [-3.361, 35.848], 'Mto wa Mbu',    'Tanzania', 'city'],
  ['karatu',         [-3.339, 35.669], 'Karatu',        'Tanzania', 'city'],
];

/** Returns { ll: [lat,lng], name, country, feature } for the first gateway
 *  keyword found in the supplied text, or null. */
export function gatewayFor(...texts) {
  const hay = texts.filter(Boolean).join(' ').toLowerCase();
  for (const [k, ll, name, country, feature] of GATEWAYS) {
    if (hay.includes(k)) return { ll, name, country, feature };
  }
  return null;
}

/* ── Named sub-locations ──────────────────────────────────────────────────
   A day's `primaryPark` is one fixed centroid per park — fine for a single
   overnight, wrong for a multi-day stay that actually moves through the
   park (a trek's named camps, or a specific concession/valley the day's
   title calls out by name). Without this, every day sharing a park
   collapses onto the exact same dot and the drawn route shows only "the
   main destination", not the itinerary's real day-by-day movement.
   Checked BEFORE primaryPark in buildStops() so a specific, named match
   always wins over the coarser park-level centroid.
   [keyword, [lat,lng], display name, country, feature type, isTrek] */
export const NAMED_PLACES = [
  // Kilimanjaro — Machame route camps (well-documented fixed camps/gates;
  // isTrek=true marks legs between them as walked, not driven).
  // Positions follow the route's real arc round the mountain's southern
  // slopes (approx., ~1 km): up from Machame Gate to Machame Camp, north-
  // west onto the Shira plateau, east under the Western Breach to Lava
  // Tower and down to Barranco, east-south-east to Karanga, up to Barafu,
  // north to the summit, then straight down the Mweka trail. The earlier
  // figures put Shira south-east of Machame Camp and Karanga north of
  // Barranco, so the walking line zig-zagged against the real route.
  ['machame gate',  [-3.1712, 37.2466], 'Machame Gate',    'Tanzania', 'camp', true],
  ['machame camp',  [-3.1027, 37.2689], 'Machame Camp',    'Tanzania', 'camp', true],
  ['shira',         [-3.0557, 37.2784], 'Shira Camp',      'Tanzania', 'camp', true],
  ['lava tower',    [-3.0713, 37.3152], 'Lava Tower',      'Tanzania', 'mountain', true],
  ['barranco',      [-3.0928, 37.3197], 'Barranco Camp',   'Tanzania', 'camp', true],
  ['karanga',       [-3.1003, 37.3394], 'Karanga Camp',    'Tanzania', 'camp', true],
  ['barafu',        [-3.0853, 37.3612], 'Barafu Camp',     'Tanzania', 'camp', true],
  ['uhuru',         [-3.0674, 37.3556], 'Uhuru Peak',      'Tanzania', 'mountain', true],
  ['mweka camp',    [-3.1336, 37.3487], 'Mweka Camp',      'Tanzania', 'camp', true],
  ['mweka gate',    [-3.2176, 37.3367], 'Mweka Gate',      'Tanzania', 'camp', true],
  // Named places distinct from their park's generic centroid. Zanzibar
  // itineraries in particular describe real movement around the island
  // (Stone Town → north-coast beaches → east-coast beaches) that a single
  // shared "Zanzibar Archipelago" primaryPark centroid would otherwise
  // flatten into one dot for the whole trip, the same "main destination
  // only" problem the Kilimanjaro camps fixed for treks.
  ['stone town',    [-6.1630, 39.1889], 'Stone Town',      'Tanzania', 'island', false],
  ['nungwi',        [-5.7260, 39.2970], 'Nungwi',          'Tanzania', 'island', false],
  ['mnarani',       [-5.7500, 39.2800], 'Mnarani',         'Tanzania', 'island', false],
  ['kendwa',        [-5.7350, 39.2850], 'Kendwa',          'Tanzania', 'island', false],
  ['east coast',    [-6.2800, 39.5100], 'East Coast Zanzibar', 'Tanzania', 'island', false],
  ['paje',          [-6.2650, 39.5040], 'Paje',            'Tanzania', 'island', false],
  ['jambiani',      [-6.4500, 39.5220], 'Jambiani',        'Tanzania', 'island', false],
];

/** Returns { ll, name, country, feature, isTrek } for the first named-place
 *  keyword found in the supplied text, or null. Order matters — more
 *  specific keywords (e.g. "machame camp") should be listed before shorter
 *  ones that could partially match ("machame gate" vs "machame"). */
export function namedPlaceFor(...texts) {
  const hay = texts.filter(Boolean).join(' ').toLowerCase();
  for (const [k, ll, name, country, feature, isTrek] of NAMED_PLACES) {
    if (hay.includes(k)) return { ll, name, country, feature, isTrek: !!isTrek };
  }
  return null;
}

/* ── Terrain/feature classification ───────────────────────────────────────
   No structured "is this a lake/mountain/marine park" field exists in the
   data model (Park.park_type is an ADMINISTRATIVE classification — National
   Park / Game Reserve / Conservation Area — not a terrain feature; nearly
   every park in the DB is "National Park" regardless of whether it's a lake,
   a crater or open savanna). Real feature icons on the map are therefore
   inferred from the place name, the same keyword-matching approach already
   used by gatewayFor() above. Order matters: check the more specific pattern
   (crater, island) before the generic ones (mountain). */
const FEATURE_PATTERNS = [
  ['crater',   /ngorongoro|crater/i],
  ['island',   /island|archipelago|zanzibar|pemba|mafia/i],
  ['mountain', /kilimanjaro|meru|mahale|mountain/i],
  ['lake',     /\blake\b/i],
];

/** Best-guess terrain feature for a park/place name — 'crater' | 'island' |
 *  'mountain' | 'lake' | 'park' (default: savanna / general wildlife area). */
export function classifyFeature(name) {
  if (!name) return 'park';
  for (const [type, re] of FEATURE_PATTERNS) if (re.test(name)) return type;
  return 'park';
}

/* routes.Location.location_type (when a day is linked to a real gazetteer
   Location row) → our internal feature-glyph vocabulary. */
const LOCATION_TYPE_FEATURE = {
  airport:  'airport',
  airstrip: 'airport',
  city:     'city',
  island:   'island',
  border:   'city',
  camp:     'camp',
  park:     'park',
};
export function featureFromLocationType(locationType) {
  return LOCATION_TYPE_FEATURE[locationType] || null;
}

export function haversineKm(a, b) {
  const R = 6371, toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b[0] - a[0]), dLng = toRad(b[1] - a[1]);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a[0])) * Math.cos(toRad(b[0])) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}
export function makeProjection(lnglats, W = 1000, H = 1000, pad = 0.12) {
  let minLng = 180, maxLng = -180, minLat = 90, maxLat = -90;
  for (const [lng, lat] of lnglats) {
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  }
  const midLat = (minLat + maxLat) / 2;
  const kx = Math.cos((midLat * Math.PI) / 180) || 1; // de-stretch longitude
  const spanX = Math.max((maxLng - minLng) * kx, 1e-4);
  const spanY = Math.max(maxLat - minLat, 1e-4);
  const s = Math.min((W * (1 - 2 * pad)) / spanX, (H * (1 - 2 * pad)) / spanY);
  const offX = (W - spanX * s) / 2;
  const offY = (H - spanY * s) / 2;
  const project = ([lng, lat]) => [
    offX + (lng - minLng) * kx * s,
    offY + (maxLat - lat) * s, // flip Y
  ];
  // `s` (px per degree of latitude) lets a caller convert a REAL-WORLD size
  // (a park's actual area, say) into a pixel size correctly scaled to THIS
  // map's zoom — the same park must draw far bigger on a tight single-region
  // map than on a map spanning two countries, even though its real area
  // hasn't changed.
  return { project, W, H, scale: s };
}
export function smoothPath(pts, tension = 0.5) {
  if (pts.length < 2) return '';
  if (pts.length === 2) return `M${pts[0][0]},${pts[0][1]} L${pts[1][0]},${pts[1][1]}`;
  const d = [`M${pts[0][0]},${pts[0][1]}`];
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] || pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] || p2;
    const c1x = p1[0] + ((p2[0] - p0[0]) / 6) * tension * 2;
    const c1y = p1[1] + ((p2[1] - p0[1]) / 6) * tension * 2;
    const c2x = p2[0] - ((p3[0] - p1[0]) / 6) * tension * 2;
    const c2y = p2[1] - ((p3[1] - p1[1]) / 6) * tension * 2;
    d.push(`C${c1x},${c1y} ${c2x},${c2y} ${p2[0]},${p2[1]}`);
  }
  return d.join(' ');
}

/** Smooth CLOSED loop through a ring of points (wraps back to the start) —
 *  a real lake shoreline is a curve, not a set of straight segments between
 *  hand-picked vertices, so this is what actually makes an approximated
 *  lake shape read as a lake instead of a faceted polygon. */
export function smoothClosedPath(pts, tension = 0.5) {
  const n = pts.length;
  if (n < 3) return smoothPath(pts, tension);
  const at = (i) => pts[((i % n) + n) % n];
  const d = [`M${pts[0][0]},${pts[0][1]}`];
  for (let i = 0; i < n; i++) {
    const p0 = at(i - 1), p1 = at(i), p2 = at(i + 1), p3 = at(i + 2);
    const c1x = p1[0] + ((p2[0] - p0[0]) / 6) * tension * 2;
    const c1y = p1[1] + ((p2[1] - p0[1]) / 6) * tension * 2;
    const c2x = p2[0] - ((p3[0] - p1[0]) / 6) * tension * 2;
    const c2y = p2[1] - ((p3[1] - p1[1]) / 6) * tension * 2;
    d.push(`C${c1x},${c1y} ${c2x},${c2y} ${p2[0]},${p2[1]}`);
  }
  return d.join(' ') + ' Z';
}

/** Waypoints for a winding road between two projected points — alternating
 *  perpendicular offsets at evenly-spaced steps along the straight chord.
 *  Feed the result into smoothPath() to draw an actual zigzagging road
 *  instead of one smooth bow (real roads switch back, they don't arc). */
export function zigzagPoints(p1, p2, segments = 5, amplitude = 0.05) {
  const [x1, y1] = p1, [x2, y2] = p2;
  const dx = x2 - x1, dy = y2 - y1;
  const len = Math.hypot(dx, dy) || 1;
  const px = -dy / len, py = dx / len;
  const pts = [p1];
  for (let i = 1; i < segments; i++) {
    const t = i / segments;
    const bx = x1 + dx * t, by = y1 + dy * t;
    const sign = i % 2 === 0 ? 1 : -1;
    pts.push([bx + px * len * amplitude * sign, by + py * len * amplitude * sign]);
  }
  pts.push(p2);
  return pts;
}

/** A single quadratic-bezier leg between two projected points, bowed away
 *  from the straight chord by `bowFrac` of the leg's length. A dead-straight
 *  line between two stops reads as a ruler cut across the map — wrong for a
 *  2-3 point route especially — so every leg gets some curvature, with
 *  flown legs bowed more (they read as "arced over", the usual map-diagram
 *  convention for flights) than short road transfers. */
export function bowLegPath(p1, p2, bowFrac, sign = 1) {
  const [x1, y1] = p1, [x2, y2] = p2;
  const dx = x2 - x1, dy = y2 - y1;
  const len = Math.hypot(dx, dy) || 1;
  const px = -dy / len, py = dx / len;
  const cx = (x1 + x2) / 2 + px * len * bowFrac * sign;
  const cy = (y1 + y2) / 2 + py * len * bowFrac * sign;
  return { d: `M${x1},${y1} Q${cx},${cy} ${x2},${y2}`, control: [cx, cy] };
}

/** Point at t (0..1) along a quadratic bezier — used to anchor a leg's
 *  transport glyph on the visual midpoint of its arc rather than the
 *  midpoint of the straight chord it's bowing away from. */
export function quadPoint(p1, control, p2, t = 0.5) {
  const mt = 1 - t;
  return [
    mt * mt * p1[0] + 2 * mt * t * control[0] + t * t * p2[0],
    mt * mt * p1[1] + 2 * mt * t * control[1] + t * t * p2[1],
  ];
}


/* ── Safari areas inside the big parks ──────────────────────────────────────
   Serengeti is 14,750 km² — "Serengeti National Park" as one centroid puts
   a Ndutu calving-season stay and a Kogatende river-crossing stay on the
   same dot. Days that name the area they're in land there instead.
   Appended to NAMED_PLACES (checked before the park centroid). */
NAMED_PLACES.push(
  ['ngorongoro crater', [-3.1700, 35.5850], 'Ngorongoro Crater',   'Tanzania', 'crater', false],
  ['crater rim',        [-3.2050, 35.5500], 'Ngorongoro Crater',   'Tanzania', 'crater', false],
  ['ndutu',             [-3.0150, 34.9950], 'Ndutu',               'Tanzania', 'park',   false],
  ['southern serengeti',[-2.9000, 34.9800], 'Southern Serengeti',  'Tanzania', 'park',   false],
  ['seronera',          [-2.4520, 34.8230], 'Central Serengeti',   'Tanzania', 'park',   false],
  ['central serengeti', [-2.4520, 34.8230], 'Central Serengeti',   'Tanzania', 'park',   false],
  ['kogatende',         [-1.5700, 34.8900], 'Northern Serengeti',  'Tanzania', 'park',   false],
  ['northern serengeti',[-1.5700, 34.8900], 'Northern Serengeti',  'Tanzania', 'park',   false],
  ['lobo',              [-2.0300, 35.1700], 'Lobo',                'Tanzania', 'park',   false],
  ['western corridor',  [-2.1800, 34.2300], 'Western Corridor',    'Tanzania', 'park',   false],
  ['grumeti',           [-2.1800, 34.2300], 'Western Corridor',    'Tanzania', 'park',   false],
  ['olduvai',           [-2.9930, 35.3510], 'Olduvai Gorge',       'Tanzania', 'park',   false],
);

/* ── Safari road network ────────────────────────────────────────────────────
   The roads a safari vehicle actually drives, as junction-to-junction
   edges. A road leg is routed along these (shortest path) instead of drawn
   as a free curve, so Tarangire → Serengeti visibly goes Makuyuni → Mto wa
   Mbu → Karatu → over the Ngorongoro rim → Naabi Hill, the only way there
   is. Coordinates are the towns/gates themselves (WGS84, ~1 km). `label`
   marks the places worth naming on the map as "via" points. */
export const ROAD_NODES = {
  kia:          { ll: [-3.4294, 37.0745], name: 'Kilimanjaro Airport' },
  kia_jct:      { ll: [-3.3770, 37.0700] },
  moshi:        { ll: [-3.3490, 37.3400], name: 'Moshi', label: true },
  usa_river:    { ll: [-3.3670, 36.8530], name: 'Usa River' },
  arusha_np:    { ll: [-3.2880, 36.8830], name: 'Ngongongare Gate' },
  arusha:       { ll: [-3.3869, 36.6830], name: 'Arusha' },
  monduli_jct:  { ll: [-3.4300, 36.3900] },
  makuyuni:     { ll: [-3.5560, 36.0930], name: 'Makuyuni', label: true },
  kwa_kuchinja: { ll: [-3.7000, 35.9600] },
  tarangire:    { ll: [-3.8350, 36.0000], name: 'Tarangire Gate' },
  escarpment:   { ll: [-3.4300, 35.9500] },
  mto_wa_mbu:   { ll: [-3.3610, 35.8480], name: 'Mto wa Mbu', label: true },
  karatu:       { ll: [-3.3390, 35.6690], name: 'Karatu', label: true },
  lodoare:      { ll: [-3.2550, 35.5880], name: 'Lodoare Gate' },
  crater_rim:   { ll: [-3.2050, 35.5500] },
  crater_floor: { ll: [-3.1700, 35.5850] },
  seneto:       { ll: [-3.1300, 35.4400] },
  olduvai:      { ll: [-2.9930, 35.3510], name: 'Olduvai Gorge', label: true },
  naabi:        { ll: [-2.8350, 34.9960], name: 'Naabi Hill Gate', label: true },
  ndutu:        { ll: [-3.0150, 34.9950] },
  simba_kopjes: { ll: [-2.6500, 34.9200] },
  seronera:     { ll: [-2.4520, 34.8230] },
  lobo:         { ll: [-2.0300, 35.1700] },
  kogatende:    { ll: [-1.5700, 34.8900] },
  kirawira:     { ll: [-2.1800, 34.2300] },
  ndabaka:      { ll: [-2.1700, 33.8800], name: 'Ndabaka Gate' },
  mwanza:       { ll: [-2.5170, 32.9000], name: 'Mwanza' },
  longido:      { ll: [-2.7300, 36.6900] },
  namanga:      { ll: [-2.5460, 36.7870], name: 'Namanga', label: true },
  amboseli_rd:  { ll: [-2.6200, 37.0000] },
  amboseli:     { ll: [-2.6800, 37.2600] },
  kajiado:      { ll: [-1.8500, 36.7900] },
  nairobi:      { ll: [-1.2860, 36.8170], name: 'Nairobi' },
  emali:        { ll: [-2.0800, 37.4700] },
  kimana:       { ll: [-2.8000, 37.5300] },
  mai_mahiu:    { ll: [-1.0100, 36.5900] },
  narok:        { ll: [-1.0800, 35.8700], name: 'Narok', label: true },
  sekenani:     { ll: [-1.5600, 35.3600], name: 'Sekenani Gate' },
  keekorok:     { ll: [-1.5900, 35.2500] },
  naivasha:     { ll: [-0.7170, 36.4300], name: 'Naivasha' },
  nakuru:       { ll: [-0.3000, 36.0700], name: 'Nakuru' },
  dar:          { ll: [-6.7920, 39.2080], name: 'Dar es Salaam' },
  chalinze:     { ll: [-6.6400, 38.3500] },
  morogoro:     { ll: [-6.8200, 37.6600], name: 'Morogoro', label: true },
  mikumi:       { ll: [-7.4000, 36.9800], name: 'Mikumi' },
  iringa:       { ll: [-7.7700, 35.6900], name: 'Iringa', label: true },
  ruaha:        { ll: [-7.7300, 34.9200], name: 'Ruaha Gate' },
};

const ROAD_EDGES = [
  ['kia', 'kia_jct'], ['kia_jct', 'moshi'], ['kia_jct', 'usa_river'], ['usa_river', 'arusha'],
  ['usa_river', 'arusha_np'], ['arusha', 'monduli_jct'], ['monduli_jct', 'makuyuni'],
  ['makuyuni', 'kwa_kuchinja'], ['kwa_kuchinja', 'tarangire'],
  ['makuyuni', 'escarpment'], ['escarpment', 'mto_wa_mbu'], ['mto_wa_mbu', 'karatu'],
  ['karatu', 'lodoare'], ['lodoare', 'crater_rim'], ['crater_rim', 'crater_floor'],
  ['crater_rim', 'seneto'], ['seneto', 'olduvai'], ['olduvai', 'naabi'], ['naabi', 'ndutu'],
  ['naabi', 'simba_kopjes'], ['simba_kopjes', 'seronera'], ['seronera', 'lobo'], ['lobo', 'kogatende'],
  ['seronera', 'kirawira'], ['kirawira', 'ndabaka'], ['ndabaka', 'mwanza'],
  ['arusha', 'longido'], ['longido', 'namanga'], ['namanga', 'kajiado'], ['kajiado', 'nairobi'],
  ['namanga', 'amboseli_rd'], ['amboseli_rd', 'amboseli'],
  ['nairobi', 'emali'], ['emali', 'kimana'], ['kimana', 'amboseli'],
  ['nairobi', 'mai_mahiu'], ['mai_mahiu', 'narok'], ['narok', 'sekenani'], ['sekenani', 'keekorok'],
  ['mai_mahiu', 'naivasha'], ['naivasha', 'nakuru'],
  ['dar', 'chalinze'], ['chalinze', 'morogoro'], ['morogoro', 'mikumi'], ['mikumi', 'iringa'], ['iringa', 'ruaha'],
];

const ROAD_ADJ = (() => {
  const adj = {};
  for (const [a, b] of ROAD_EDGES) {
    const w = haversineKm(ROAD_NODES[a].ll, ROAD_NODES[b].ll);
    (adj[a] = adj[a] || []).push([b, w]);
    (adj[b] = adj[b] || []).push([a, w]);
  }
  return adj;
})();

const SNAP_KM = 70;

function nearestNode(ll) {
  let best = null, bestD = Infinity;
  for (const [id, n] of Object.entries(ROAD_NODES)) {
    const d = haversineKm(ll, n.ll);
    if (d < bestD) { bestD = d; best = id; }
  }
  return bestD <= SNAP_KM ? best : null;
}

/** The road between two points, as the junctions/towns it passes through
 *  ([{ ll, name, label }] — endpoints excluded), or null when either end
 *  is off the network (then the leg is drawn as before). */
export function roadVia(fromLL, toLL) {
  const a = nearestNode(fromLL), b = nearestNode(toLL);
  if (!a || !b || a === b) return null;
  // Dijkstra — the graph is a few dozen nodes.
  const dist = { [a]: 0 }, prev = {}, done = new Set();
  while (true) {
    let u = null;
    for (const k of Object.keys(dist)) if (!done.has(k) && (u === null || dist[k] < dist[u])) u = k;
    if (u === null || u === b) break;
    done.add(u);
    for (const [v, w] of ROAD_ADJ[u] || []) {
      const nd = dist[u] + w;
      if (dist[v] === undefined || nd < dist[v]) { dist[v] = nd; prev[v] = u; }
    }
  }
  if (dist[b] === undefined) return null;
  const path = [];
  for (let n = b; n; n = prev[n]) path.unshift(n);
  // Drop junctions that sit on top of either endpoint — they'd only add a kink.
  return path
    .map((id) => ({ id, ...ROAD_NODES[id] }))
    .filter((n) => haversineKm(n.ll, fromLL) > 6 && haversineKm(n.ll, toLL) > 6);
}
